/* ----------------------------------------------------------------------------
 * AuraDashboardLauncher — Aura's PRIMARY home on the Executive OS (owner
 * reference redesign 2026-07-18).
 *
 * The dashboard hero is a full-width, THEME-AWARE card (approved light card in
 * light mode, approved dark card in dark mode) with a subtle warm-amber glow
 * border. There is NO orb on the dashboard — the single Aura mark lives only in
 * the popup header. The hero carries the "AURA / VOICE" label, an
 * "Ask me anything, <name>" heading, a short blurb, and a right-aligned
 * "Talk to Aura" button whose amber animated waveform signals voice. Clicking it
 * opens the focused voice popup (VoiceCopilot: push-to-talk, spoken reply, text
 * fallback).
 *
 * State visuals (2026-07-19): while the popup is Listening or Speaking, a
 * <HeroStateWave/> flows an audio-wave BEHIND the hero text — teal for
 * listening, warm orange/coral/magenta for speaking. Thinking and idle show NO
 * wave. The wave is driven by the popup's live status (reported via
 * onStatusChange); it sits behind the text (z-0), is pointer-events:none, and
 * never affects layout. The "Talk to Aura" button is UNCHANGED.
 *
 * Fail-closed: it shares the ['voice-status'] probe; when voice is unavailable
 * the button is disabled + greyed with an honest reason — never a live call.
 * -------------------------------------------------------------------------- */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { VoiceCopilot } from './VoiceCopilot.tsx';
import { AuraWaveform } from './AuraWaveform.tsx';
import { HeroStateWave } from './state-visuals/HeroStateWave.tsx';
import { getVoiceStatus } from './net/voiceApi.ts';
import type { VoiceProviderConfig } from './providers/registry.ts';
import type { VoiceStatus } from './types.ts';

export interface AuraDashboardLauncherProps {
  /** the operator's first name for a warm greeting (optional). */
  greetingName?: string;
  className?: string;
}

const AMBER = '#f59e0b';

export function AuraDashboardLauncher({ greetingName, className }: AuraDashboardLauncherProps) {
  const [open, setOpen] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>('idle');

  const voiceStatusQuery = useQuery({
    queryKey: ['voice-status'],
    queryFn: () => getVoiceStatus(),
    retry: false,
    staleTime: 60_000,
  });
  const voiceAvailable = voiceStatusQuery.data?.voice_available === true;
  const reason = voiceStatusQuery.data?.reason;
  const voiceConfig: VoiceProviderConfig = {
    stt: voiceStatusQuery.data?.stt_provider ?? 'mock',
    tts: voiceStatusQuery.data?.tts_provider ?? 'mock',
    ttsVoiceId: voiceStatusQuery.data?.tts_voice_id ?? 'aura-2-pandora-en',
  };

  useBodyScrollLock(open);
  const trapRef = useFocusTrap<HTMLDivElement>(open, () => setOpen(false));

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // When the popup closes, the hero wave must stop immediately (no lingering).
  useEffect(() => {
    if (!open) setVoiceStatus('idle');
  }, [open]);

  const name = greetingName ?? 'Phoenix';
  const invite = `Ask me anything, ${name}`;
  // Hero wave mirrors the live voice status only while the popup is open.
  const heroStatus: VoiceStatus = open ? voiceStatus : 'idle';

  return (
    <>
      {/* Dashboard hero — dark card, subtle warm-amber glow border. No orb. */}
      <section
        aria-label="Aura voice assistant"
        className={cn('relative overflow-hidden rounded-2xl bg-surface p-5 sm:p-6', className)}
        style={{
          border: `1px solid ${AMBER}33`,
          boxShadow: `0 0 0 1px ${AMBER}0f, 0 18px 48px -24px ${AMBER}59`,
        }}
      >
        {/* State-driven audio-wave — behind the text, listening/speaking only. */}
        <HeroStateWave status={heroStatus} />

        <div className="relative z-10 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: 'rgb(var(--ai-accent-text))' }}>
                Aura
              </span>
              <span
                className="rounded-md px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
                style={{ color: '#ff8c42', border: '1px solid #ff8c4266' }}
              >
                Voice
              </span>
            </div>
            <h2 className="mt-2 text-[26px] font-semibold leading-tight text-text-primary sm:text-[28px]">
              {voiceAvailable ? invite : 'Aura is getting ready'}
            </h2>
            <p className="mt-2 max-w-[60ch] text-[15px] leading-relaxed text-text-secondary">
              {voiceAvailable
                ? 'I\'m your executive assistant, available 24/7. Ask me what needs your attention across sales, marketing or customer care, then decide what happens.'
                : `Voice isn’t available on this environment yet${reason ? ` (${reason})` : ''}. Aura’s text copilot is always in the side rail.`}
            </p>
          </div>

          {/* Talk to Aura — amber pill, animated waveform, soft amber glow. */}
          <div className="shrink-0">
            <button
              type="button"
              onClick={() => voiceAvailable && setOpen(true)}
              disabled={!voiceAvailable}
              aria-label={voiceAvailable ? 'Open Aura voice' : 'Aura voice unavailable'}
              title={voiceAvailable ? 'Talk to Aura' : `Voice unavailable${reason ? ` — ${reason}` : ''}`}
              className={cn(
                'group inline-flex items-center gap-2.5 rounded-full px-5 py-2.5 text-[15px] font-semibold outline-none transition-all duration-fast',
                voiceAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
              )}
              style={
                voiceAvailable
                  ? { color: 'rgb(var(--ai-accent-text))', background: `${AMBER}0d`, border: `1.5px solid ${AMBER}`, boxShadow: `0 0 18px -2px ${AMBER}59` }
                  : { color: 'rgb(var(--text-tertiary))', background: 'rgb(var(--bg-inset))', border: '1.5px solid rgb(var(--border-default))' }
              }
            >
              <AuraWaveform
                active={voiceAvailable}
                bars={4}
                heightPx={15}
                className={voiceAvailable ? undefined : 'opacity-60'}
              />
              Talk to Aura
            </button>
          </div>
        </div>
      </section>

      {/* Focused voice popup — centred modal (~460px). */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 animate-fade-in p-3 backdrop-blur-sm sm:items-center"
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            ref={trapRef}
            role="dialog"
            aria-modal
            aria-label="Aura voice"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[460px] pb-[env(safe-area-inset-bottom)] outline-none sm:pb-0"
          >
            <VoiceCopilot config={voiceConfig} onClose={() => setOpen(false)} onStatusChange={setVoiceStatus} />
          </div>
        </div>
      )}
    </>
  );
}
